import React__default, { isValidElement } from 'react';
import { ButtonGroup, Text, Icon, UnstyledButton, Button as Button$1 } from '@shopify/polaris';
import { IS_PROD_ENV } from '../../../../utilities/env.mjs';
import styles from './Header.scss.mjs';
import { useCompoundComponentProviderContext } from '../CompoundComponents/components/CompoundComponentProvider/CompoundComponentProvider.mjs';
import DragHandleIcon from '../../icons/DragHandle.svg.mjs';

let ButtonTypeValue = /*#__PURE__*/function (ButtonTypeValue) {
  ButtonTypeValue["Primary"] = "Primary";
  ButtonTypeValue["Secondary"] = "Secondary";
  ButtonTypeValue["MoreActions"] = "MoreActions";
  return ButtonTypeValue;
}({});
function Header({
  expanded,
  onTap,
  actionButtonWidth
}) {
  const context = useCompoundComponentProviderContext();
  const {
    buttons,
    visuallyHiddenTitles
  } = context;
  const {
    secondaryActionMarkup,
    primaryActionMarkup,
    moreActionsMarkup
  } = getActionMarkup(buttons);
  const actionsMarkup = /*#__PURE__*/React__default.createElement("div", {
    className: styles.PrimaryActionGroup
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.PrimaryActionGroupWrapper
  }, /*#__PURE__*/React__default.createElement(ButtonGroup, {
    variant: "segmented"
  }, primaryActionMarkup, moreActionsMarkup)));
  function renderMiddleActionWrapper(markup) {
    if (onTap) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: styles.MiddleActionInteractive
      }, /*#__PURE__*/React__default.createElement(UnstyledButton, {
        className: styles.MiddleActionButton,
        onClick: onTap,
        ariaExpanded: expanded
      }, markup));
    } else {
      return /*#__PURE__*/React__default.createElement("div", {
        className: styles.MiddleAction
      }, markup);
    }
  }
  return /*#__PURE__*/React__default.createElement("nav", {
    className: styles.Header
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.Interior,
    style: actionButtonWidth ? {
      gridTemplateColumns: `minmax(${actionButtonWidth}, 1fr) auto minmax(${actionButtonWidth}, 1fr)`
    } : undefined
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.SecondaryAction
  }, secondaryActionMarkup), renderMiddleActionWrapper( /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: styles.DragHandleIcon
  }, /*#__PURE__*/React__default.createElement(DragHandleIcon, null)), /*#__PURE__*/React__default.createElement("div", {
    className: styles.Title
  }, /*#__PURE__*/React__default.createElement(Title, null), getAccessibilityMarkup(visuallyHiddenTitles)))), actionsMarkup));
  function getActionMarkup(buttons) {
    const actionMarkup = {
      secondaryActionMarkup: null,
      primaryActionMarkup: null,
      moreActionsMarkup: null
    };
    if (!IS_PROD_ENV && buttons.size > 3) {
      console.warn('BottomSheet can only render 3 Buttons per Sheet');
    }
    const buttonsArray = Array.from(buttons.values());
    buttonsArray.forEach(button => {
      if (!IS_PROD_ENV && !button.content && !button.accessibilityLabel) {
        console.warn(`You did not provide an 'accessibilityLabel' to an icon-only variant of BottomSheet ${button.type} Button`);
      }
      if (button.type === ButtonTypeValue.Primary) {
        actionMarkup.primaryActionMarkup = /*#__PURE__*/React__default.createElement(Button, {
          button: button,
          isSheetExpanded: expanded,
          onSheetExpandChange: onTap
        });
      }
      if (button.type === ButtonTypeValue.MoreActions) {
        actionMarkup.moreActionsMarkup = /*#__PURE__*/React__default.createElement(Button, {
          button: button,
          isSheetExpanded: expanded,
          onSheetExpandChange: onTap
        });
      }
      if (button.type === ButtonTypeValue.Secondary) {
        actionMarkup.secondaryActionMarkup = /*#__PURE__*/React__default.createElement(Button, {
          button: button,
          isSheetExpanded: expanded,
          onSheetExpandChange: onTap
        });
      }
    });
    return actionMarkup;
  }
}
function Title() {
  var _titleOptions$prefix, _titleOptions$suffix, _subtitleOptions$pref, _subtitleOptions$suff;
  const context = useCompoundComponentProviderContext();
  if (!context) {
    return null;
  }
  const {
    titles
  } = context;
  if (!titles) {
    return null;
  }

  // TODO: Revisit if slicing is still required
  // https://github.com/Shopify/online-store-ui/issues/3648
  const currentPanelTitle = Array.from(titles.values()).slice(-1)[0];
  if (!currentPanelTitle) {
    return null;
  }
  const title = currentPanelTitle.title;
  if ( /*#__PURE__*/isValidElement(title)) {
    // title is a React element
    return title;
  }
  const subtitle = currentPanelTitle.subtitle;
  const titleOptions = typeof title === 'string' ? {
    content: title
  } : title;
  const subtitleOptions = typeof subtitle === 'string' ? {
    content: subtitle
  } : subtitle;

  // TODO: Consider breaking up Title and Subtitle into separate components
  // https://github.com/Shopify/online-store-ui/issues/3648
  const titleMarkup = titleOptions != null && titleOptions.content ? /*#__PURE__*/React__default.createElement(Text, {
    variant: "headingMd",
    as: "h2",
    tone: titleOptions.subdued ? 'subdued' : undefined,
    truncate: true
  }, titleOptions.content) : null;
  const subtitleMarkup = subtitleOptions != null && subtitleOptions.content ? /*#__PURE__*/React__default.createElement(Text, {
    variant: "bodySm",
    as: "p",
    tone: "subdued",
    truncate: true
  }, subtitleOptions.content) : null;
  const titlePrefixMarkup = /*#__PURE__*/React__default.createElement("div", {
    className: styles.TitlePrefixWrapper
  }, (titleOptions == null ? void 0 : (_titleOptions$prefix = titleOptions.prefix) == null ? void 0 : _titleOptions$prefix.source) && /*#__PURE__*/React__default.createElement(Icon, titleOptions.prefix));
  const titleSuffixMarkup = /*#__PURE__*/React__default.createElement("div", {
    className: styles.SuffixWrapper
  }, (titleOptions == null ? void 0 : (_titleOptions$suffix = titleOptions.suffix) == null ? void 0 : _titleOptions$suffix.source) && /*#__PURE__*/React__default.createElement(Icon, titleOptions.suffix));
  const subtitlePrefixMarkup = /*#__PURE__*/React__default.createElement("div", {
    className: styles.SubtitlePrefixWrapper
  }, (subtitleOptions == null ? void 0 : (_subtitleOptions$pref = subtitleOptions.prefix) == null ? void 0 : _subtitleOptions$pref.source) && /*#__PURE__*/React__default.createElement(Icon, Object.assign({}, subtitleOptions.prefix, {
    tone: "subdued"
  })));
  const subtitleSuffixMarkup = /*#__PURE__*/React__default.createElement("div", {
    className: styles.SubtitleSuffixWrapper
  }, (subtitleOptions == null ? void 0 : (_subtitleOptions$suff = subtitleOptions.suffix) == null ? void 0 : _subtitleOptions$suff.source) && /*#__PURE__*/React__default.createElement(Icon, Object.assign({}, subtitleOptions.suffix, {
    tone: "subdued"
  })));
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: styles.TitleWrapper
  }, titlePrefixMarkup, titleMarkup, titleSuffixMarkup), subtitleOptions ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.SubtitleWrapper
  }, subtitlePrefixMarkup, subtitleMarkup, subtitleSuffixMarkup) : null);
}
function getAccessibilityMarkup(visuallyHiddenTitles) {
  if (!visuallyHiddenTitles) {
    return null;
  }
  const visuallyHiddenTitlesArray = Array.from(visuallyHiddenTitles.values());
  return visuallyHiddenTitlesArray.map(title => {
    return /*#__PURE__*/React__default.createElement(VisuallyHiddenTitle, {
      key: `${title.id}-${title.title}`,
      title: title.title,
      isSubtitle: title.isSubtitle
    });
  });
}
function VisuallyHiddenTitle({
  title,
  isSubtitle
}) {
  return /*#__PURE__*/React__default.createElement(Text, {
    variant: "bodySm",
    as: isSubtitle ? 'p' : 'h2',
    visuallyHidden: true
  }, title);
}
function Button({
  isSheetExpanded,
  onSheetExpandChange,
  button
}) {
  if (!button) {
    return null;
  }
  const {
    onAction,
    content,
    prefixIcon,
    type,
    loading,
    ...buttonProps
  } = button;
  function handleOnActionExpand() {
    if (!isSheetExpanded) {
      onSheetExpandChange == null ? void 0 : onSheetExpandChange();
    }
    onAction == null ? void 0 : onAction();
  }
  return /*#__PURE__*/React__default.createElement(Button$1, Object.assign({
    variant: "tertiary",
    fullWidth: true,
    size: "large",
    onClick: handleOnActionExpand,
    icon: prefixIcon,
    textAlign: type === ButtonTypeValue.Secondary ? 'left' : 'right',
    loading: loading
  }, buttonProps), content);
}

export { Button, ButtonTypeValue, Header, Title, VisuallyHiddenTitle, getAccessibilityMarkup };
